import { useCallback, useEffect, useRef } from 'react'

// Browsers use document.title as the default "Save as PDF" / print filename.
// This temporarily swaps in a descriptive filename around printing, then
// restores the original title. Both the in-app Print button (via the returned
// callback) and native Cmd+P (via the `beforeprint` listener) are covered;
// applying the title is idempotent so the two paths don't double-save the
// original. A falsy filename leaves the title untouched.
export function usePrintWithDocumentTitle(
  filename: string | undefined
): () => void {
  const filenameRef = useRef(filename)
  filenameRef.current = filename
  // Non-null while the descriptive title is active, holding the title to restore.
  const previousTitleRef = useRef<string | null>(null)

  const applyPrintTitle = useCallback(() => {
    if (previousTitleRef.current !== null || !filenameRef.current) {
      return
    }
    previousTitleRef.current = document.title
    document.title = filenameRef.current
  }, [])

  const restoreTitle = useCallback(() => {
    if (previousTitleRef.current === null) {
      return
    }
    document.title = previousTitleRef.current
    previousTitleRef.current = null
  }, [])

  useEffect(() => {
    window.addEventListener('beforeprint', applyPrintTitle)
    window.addEventListener('afterprint', restoreTitle)
    return () => {
      window.removeEventListener('beforeprint', applyPrintTitle)
      window.removeEventListener('afterprint', restoreTitle)
      // Defensively restore in case the component unmounts mid-print.
      restoreTitle()
    }
  }, [applyPrintTitle, restoreTitle])

  return useCallback(() => {
    applyPrintTitle()
    window.print()
  }, [applyPrintTitle])
}
